import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Params } from '@angular/router';
import { ChartsService } from '@app/charts/charts.service';
import { LearningChartService } from '@app/charts/components/learning-chart/learning-chart.service';
import { RouterComponents } from '@degreed/lxp/org/models';
import { SubscriptionQueueService } from '@degreed/lxp/services';
import { AuthService } from '@degreed/lxp/services/authorization';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OrgReportingBaseComponent } from './org-reporting-base.component';

interface LearningDataWithType {
  dataType: string;
  [key: string]: unknown;
}
@Component({
  selector: 'dgx-org-reporting',
  templateUrl: './org-reporting.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [SubscriptionQueueService],
})
export class OrgReportingComponent
  extends OrgReportingBaseComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  private learningChartService = inject(LearningChartService);

  // dgxLearningChart component Output binding
  @Output() public getChartData: EventEmitter<{
    params: any;
  }> = new EventEmitter<{ params: any }>();
  public readonly CLASS_NAME = RouterComponents.ORG_REPORTING_COMPONENT;
  public override resolver: any;

  public get typedReportingParams() {
    return this.reportingParams as Params;
  }
  constructor(public override cdr: ChangeDetectorRef) {
    super(inject(AuthService), inject(Injector), cdr);
  }

  /**
   * Property to determine whether or not to display the dgRecommendationsStatus component
   */
  public get hasRecommendationsStatus(): boolean {
    let valid = false;
    const showMore = !this.showMore || this.showMore === 'recommendations';
    const hasPermission = this.permissions.canViewReports;
    const singleGroupIdSelected =
      this.groupIdList.split(',').filter((id) => !isNaN(parseInt(id, 10)))
        .length === 1;
    if (showMore && hasPermission && singleGroupIdSelected) {
      valid = true;
    }
    return valid;
  }

  /**
   * Property to determine whether or not to display the dgxTopGroups component
   */
  public get hasTopGroups(): boolean {
    let valid = false;
    const showMore = !this.showMore || this.showMore === 'topGroups';
    const hasPermission =
      this.permissions.canViewReports || this.permissions.canManageGroups;
    if (showMore && hasPermission) {
      valid = true;
    }
    return valid;
  }

  public ngAfterViewInit(): void {
    this.loadChartsModule();
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    // Combine both streams - direct chart data requests and org selection triggers
    this.getChartData
      .pipe(
        switchMap((event) => {
          this.kpisLoading = true;
          return forkJoin({
            chartData: this.createLearningSummaryObservable(event),
            kpis: this.getKpis(),
          });
        }),
        this.takeUntilDestroyed()
      )
      .subscribe({
        next: ({ chartData, kpis }) => {
          const chartDataType = chartData.dataType;
          const transformedData =
            this.learningChartService.transformDataToPascal(chartData);
          const processedChartData = this.learningChartService.extractChartData(
            transformedData,
            chartDataType
          );
          this.totalDisplayData = processedChartData;
          this.kpis = kpis;
          this.kpisLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.totalDisplayData = null;
          this.kpisLoading = false;
          this.kpisError = true;
          this.cdr.detectChanges();
        },
      });
  }
  public override ngOnDestroy() {
    super.ngOnDestroy();
  }

  // channel context
  // called in the template when tenant org is changed
  public onOrgSelection(selectedItem): void {
    this.setCurrentOrgById(selectedItem.organizationId);
    const event = {
      params: {
        dataType: this.learningChartService.selectedDataType.type,
        inputType: this.learningChartService.selectedInputType.type,
        period: this.learningChartService.selectedPeriod.Value,
        tag: this.learningChartService.selectedTag,
      },
    };
    this.getChartData.emit(event);
  }
  private createLearningSummaryObservable($event: {
    params: any;
  }): Observable<LearningDataWithType> {
    const params = $event.params;
    const orgId = this.getOrgId();
    const startDate = this.startDate.value;
    const endDate = this.endDate.value;
    const groupIdAdjust = this.adjustGroup(this.groupId);

    if (this.reportingContentService.isInputType(params.inputType)) {
      params.inputType = [params.inputType];
    }

    // this is weird it takes in a tag ({name:string}) but returns a string to the same object
    params.tag = this.reportingContentService.setTagWhenDefault(params.tag);

    const isViewsDataType =
      params.dataType === this.dataTypes.views ||
      params.dataType === this.dataTypes.Views;

    const baseObservable = (
      isViewsDataType
        ? this.reportingService.getTotalContentViewed(
            orgId,
            startDate,
            endDate,
            params.inputType,
            params.tag,
            groupIdAdjust,
            this.groupIdList
          )
        : this.reportingService.getInsights(
            orgId,
            startDate,
            endDate,
            params.inputType,
            params.tag,
            groupIdAdjust,
            this.groupIdList
          )
    ) as Observable<Record<string, unknown>>;

    return baseObservable.pipe(
      map((data): LearningDataWithType => ({
        ...data,
        dataType: params.dataType,
      }))
    );
  }
  public trackInsightFilter(type, event): void {
    this.trackFilter(
      'Org Skill Insights Filtered',
      'Org Skill Insights',
      type,
      event.target.innerText
    );
  }

  private adjustGroup(groupId): any {
    return groupId === 0 ? null : groupId;
  }
  /**
   * Lazy load Charts module
   */
  private loadChartsModule(): void {
    ChartsService.loadModule().then((m) => {
      this.resolver = m.ComponentResolver;
      this.cdr.detectChanges();
    });
  }
  public showChart(name: string) {
    return true;
  }
  public getComponent(name: string): any {
    if (this.resolver) {
      return this.resolver.getComponent(name);
    }
    return null;
  }
  public getChartInputs(name: string) {
    let config = {};
    switch (name) {
      case 'dgxActiveUserChart':
        config = {
          startDate: this.startDate,
          endDate: this.endDate,
          selectedPeriod: this.selectedPeriod,
          orgId: this.orgId,
          hasDrillDown: false,
          groupId: this.groupId,
          groupIdList: this.groupIdList,
          location: 'Org Insights',
        };
        break;
      case 'dgxReportingRangePicker':
        config = {
          appearance: 'dropdown',
          selection: this.updateDateRange,
          value: this.dateRange,
          location: 'Org Insights',
          maxDays: 366,
        };
        break;
      case 'dgxLearningChart':
        config = {
          startDate: this.startDate,
          endDate: this.endDate,
          chart: this.totalDisplayData,
          selectedPeriod: this.selectedPeriod,
          setSelectedPeriod: false,
          orgId: this.orgId,
          hasDrillDown: this.context !== 'channel',
          groupId: this.groupId,
          groupIdList: this.groupIdList,
          context: this.context,
          location: 'Org Insights',
          getChartData: this.getChartData,
        };
        break;
      case 'dgxSkillRatingsChart':
        config = {
          orgId: this.orgId,
          startDate: this.startDate.value,
          endDate: this.endDate.value,
          groupId: this.groupId,
          groupIdList: this.groupIdList,
          moreId: 'skillRatings',
          context: this.context,
          location: 'Org Insights',
        };
        break;
      case 'dgxTopCompletionsChart':
        config = {
          orgId: this.orgId,
          startDate: this.startDate.value,
          endDate: this.endDate.value,
          groupId: this.groupId,
          groupIdList: this.groupIdList,
          permissions: this.permissions,
          location: 'Org Insights',
        };
        break;
      case 'dgxPopularDataChart':
        config = {
          orgId: this.orgId,
          startDate: this.startDate.value,
          endDate: this.endDate.value,
          groupId: this.groupId,
          groupIdList: this.groupIdList,
          location: 'Org Insights',
        };
        break;
    }
    return config;
  }

  public getKpis(): Observable<any[]> {
    const orgId = this.getOrgId();
    const groupId = this.groupId > 0 ? this.groupId : '';

    return this.reportingService
      .getInsightKPIs(
        orgId,
        this.startDate.value,
        this.endDate.value,
        groupId,
        this.groupIdList
      )
      .pipe(map((data) => this.processKpisData(data)));
  }

  private processKpisData(data: any[]): any[] {
    const kpis = [];
    for (let i = 0; i < data.length; i++) {
      const kpi = data[i];

      const kpiItem = {
        name: kpi.name,
        Location: 'Org Insights',
        metric: kpi.metric.toLocaleString(this.localizationService.userLocale),
        trend: kpi.trend || 0,
        description: this.translate.instant(
          'OrgReportingCtrl_Kpi' + kpi.name + 'Name'
        ),
        tooltip: this.translate.instant(
          'OrgReportingCtrl_Kpi' + kpi.name + 'Tooltip'
        ),
        order: i,
        uid: i, // for track by
        numOfDays: this.reportingService.getNumberOfDays(
          this.startDate.value,
          this.endDate.value
        ),
      };
      switch (kpi.name) {
        case 'ItemsAdded':
          kpiItem.order = 2;
          break;
        case 'Recommendations':
          kpiItem.order = 3;
          break;
      }
      kpis.push(kpiItem);
    }
    return kpis;
  }
}
